import React, { useState } from 'react'
import { Box } from '@mui/material'
import { v4 as uuidv4 } from 'uuid'

import { Button, Icon, IconButton, TextField, Typography } from '~components/internal'
import { lightTokens, typography } from '~tokens/internal'
import { type AttributionCreate } from '~types'

import './AttributionInputTable.scss'

interface AttributionInputRow {
  id: string
  name: string
  value: string
  isValid: boolean
}

type AttributionInputTableProps = {
  initialAttributions?: AttributionCreate[]
  setAttributions: React.Dispatch<React.SetStateAction<AttributionCreate[]>>
  onValidationChange: (isValid: boolean) => void
}

const AttributionInputTable: React.FC<AttributionInputTableProps> = ({
  initialAttributions,
  setAttributions,
  onValidationChange,
}) => {
  const [inputRows, setInputRows] = React.useState<AttributionInputRow[]>(
    initialAttributions && initialAttributions.length > 0
      ? initialAttributions.map((attr) => ({
          id: attr.attribute_id || uuidv4(),
          name: attr.name,
          value: attr.value,
          isValid: attr.name.trim() !== '' && attr.value.trim() !== '',
        }))
      : [{ id: uuidv4(), name: '', value: '', isValid: false }],
  )
  const [blurredRows, setBlurredRows] = useState<Record<string, boolean>>({})

  const _areAllAttributionsValid = React.useMemo(() => {
    const isValid = inputRows.every(
      (row) =>
        (row.name.trim() === '' && row.value.trim() === '') ||
        (row.name.trim() !== '' && row.value.trim() !== ''),
    )

    onValidationChange?.(isValid)
    return isValid
  }, [inputRows, onValidationChange])

  const addAttributionInput = (): void => {
    setInputRows((prev) => [...prev, { id: uuidv4(), name: '', value: '', isValid: false }])
  }

  const removeAttributionInput = (id: string): void => {
    setInputRows((prev) => {
      const newRows = prev.filter((row) => row.id !== id)
      const validAttributions = newRows
        .filter((row) => row.name.trim() !== '' && row.value.trim() !== '')
        .map((row) => ({
          attribute_id: row.id,
          name: row.name,
          value: row.value,
        }))
      setAttributions(validAttributions)
      return newRows
    })
  }

  const handleBlur = (id: string): void => {
    setBlurredRows((prev) => ({ ...prev, [id]: true }))
  }

  const showError = (row: AttributionInputRow): boolean => {
    if (!blurredRows[row.id]) return false
    return (
      (row.name.trim() !== '' && row.value.trim() === '') ||
      (row.name.trim() === '' && row.value.trim() !== '')
    )
  }

  const handleAttributionChange = (id: string, field: 'name' | 'value', newValue: string): void => {
    setInputRows((prev) => {
      const newRows = prev.map((row) => {
        if (row.id !== id) return row
        const updatedRow = { ...row, [field]: newValue }
        updatedRow.isValid = updatedRow.name.trim() !== '' && updatedRow.value.trim() !== ''
        return updatedRow
      })

      const validAttributions = newRows
        .filter((row) => row.isValid)
        .map((row) => ({
          attribute_id: row.id,
          name: row.name,
          value: row.value,
        }))

      setAttributions(validAttributions)
      return newRows
    })
  }

  return (
    <Box className='attribution-input-table'>
      <Box className='attribution-input-table__list'>
        {inputRows.map((row, index) => (
          <Box key={row.id}>
            <Box className='attribution-input-table__row'>
              <Box className='attribution-input-table__fields'>
                <Box className='attribution-input-table__field'>
                  <TextField
                    label={index === 0 ? 'Additional Data' : undefined}
                    placeholder='Label'
                    value={row.name}
                    onChange={(e) => handleAttributionChange(row.id, 'name', e.target.value)}
                    onBlur={() => handleBlur(row.id)}
                    error={showError(row)}
                    fullWidth
                  />
                </Box>
                <Box className='attribution-input-table__field'>
                  <TextField
                    label={index === 0 ? '\u00A0' : undefined}
                    placeholder='Value'
                    value={row.value}
                    onChange={(e) => handleAttributionChange(row.id, 'value', e.target.value)}
                    onBlur={() => handleBlur(row.id)}
                    error={showError(row)}
                    fullWidth
                  />
                </Box>
              </Box>
              {inputRows.length > 1 && (
                <IconButton
                  size='small'
                  onClick={() => removeAttributionInput(row.id)}
                  className={index === 0 ? 'attribution-input-table__remove-button-first' : ''}
                >
                  <Icon name='Remove' size={typography.fontSize.lg} />
                </IconButton>
              )}
            </Box>
            {showError(row) && (
              <Box className='attribution-input-table__row-error'>
                <Icon name='Error' size={typography.fontSize.md} color={lightTokens.error.main} />
                <Typography variant='body2' color='error.dark'>
                  Both label and value are required
                </Typography>
              </Box>
            )}
          </Box>
        ))}
        <Button
          variant='text'
          size='small'
          width='fit'
          startIcon={<Icon name='AddCircleOutlined' />}
          onClick={addAttributionInput}
        >
          Add another
        </Button>
      </Box>
    </Box>
  )
}

export default AttributionInputTable
